/**
 * MOJE OSTRVO — the screen.
 *
 * One canvas, two modes. PROŠETAJ walks it with the game's own engine; UREDI rearranges it with the
 * same builder canvas the admin draws curriculum islands on. Neither is a preview of the other,
 * which is the point: what you arrange is what you walk on, and there is no publish step between.
 *
 * What is deliberately not on this screen: any number. No count of decorations, no measure of how
 * full the island is, no visitors, no score, no rating and nothing that could be compared with
 * anybody else's. GDD §2.1 allows this product exactly two numbers and neither of them is about
 * furniture; `tests/dom.test.ts` reads `core/dom.ts` and fails if a third appears.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DOM_SIZE,
  DOM_MAX_PROPS,
  DOM_WORDS_A,
  DOM_WORDS_B,
  domName,
  domPropKinds,
  formatDomCode,
  looksLikeDomCode,
  normaliseDomCode,
  type DomDoor,
  type RawDom,
} from '../../core/dom';
import type { PropId, ResolvedIsland } from '../../core/content';
import { copy } from '../../core/copy';
import { decodeLook } from '../../core/look';
import {
  Terrain,
  decodeTerrain,
  encodeTerrain,
  isSolid,
  isWater,
} from '../../core/terrain';
import { earnedSouvenirs } from '../../core/rules';
import { PROPS, bake, propArt, spriteColors } from '../art/sprites';
import { Btn } from '../components';
import { Engine, type Direction, type IslandInfo, type MarkerInfo } from '../game/engine';
import { Uzivo, type SocketLike } from '../game/live';
import { TILE, buildWorld } from '../game/world';
import { api } from '../api';
import { sfx } from '../audio';
import {
  contentOf,
  doorDom,
  editDom,
  flushDom,
  loadDom,
  nameDom,
  playerOf,
  revertDom,
  useStore,
} from '../store';
import { BuilderCanvas, type BuilderTool, type Tile } from './builder/Canvas';
import { PropChip, TerrainPalette } from './builder/palette';

/**
 * What a member may paint.
 *
 * Everything except the two that are not scenery: `Deep` is the open ocean the archipelago sits in,
 * and `Fog` is literally the edge of the world. Neither belongs in the hands of somebody decorating
 * a beach, and leaving them out is not withholding — they are not things, they are the frame.
 */
const DOM_TERRAIN: readonly Terrain[] = [
  Terrain.Sea,
  Terrain.Shallow,
  Terrain.Sand,
  Terrain.Ground,
  Terrain.Path,
  Terrain.Flowers,
  Terrain.TallGrass,
  Terrain.Bush,
  Terrain.Tree,
  Terrain.Rock,
  Terrain.Cliff,
  Terrain.Bridge,
  Terrain.Dock,
  Terrain.Fence,
  Terrain.Hut,
];

/** The member's toolset: no dropper-into-nowhere, no rectangle. Four verbs is plenty. */
const DOM_TOOLS: Array<{ id: BuilderTool; label: string }> = [
  { id: 'cetka', label: copy.teren.toolBrush },
  { id: 'kanta', label: copy.teren.toolFill },
  { id: 'objekti', label: copy.teren.toolObjects },
  { id: 'gumica', label: copy.teren.toolErase },
];

export function Dom() {
  const dom = useStore((s) => s.dom);
  const busy = useStore((s) => s.domBusy);
  const save = useStore((s) => s.domSave);
  const server = useStore((s) => s.server);
  const [mode, setMode] = useState<'hod' | 'uredi'>('hod');
  const [visiting, setVisiting] = useState<string | null>(null);

  useEffect(() => {
    void loadDom(visiting ?? undefined);
  }, [visiting]);

  // Leaving the screen is the moment worth writing down, the same argument the map makes about a
  // remembered position: the debounce is for typing speed, not for durability.
  useEffect(() => () => void flushDom(), []);

  if (!server) return null;

  if (!dom)
    return (
      <div className="dom">
        <p className="tiny muted">{busy ? copy.dom.saving : copy.error.genericBody}</p>
      </div>
    );

  const name = domName(dom.ime[0], dom.ime[1]);
  const host = dom.domacin;

  return (
    <div className="dom">
      <div className="dom-top">
        <b className="dom-title">{host ? copy.dom.guestOf(host.name) : copy.dom.title}</b>
        <span className="dom-name">{name}</span>
        {dom.mine ? (
          <span className="dom-modes" role="group">
            <button
              type="button"
              className={`wb-tool ${mode === 'hod' ? 'on' : ''}`.trim()}
              aria-pressed={mode === 'hod'}
              onClick={() => {
                void flushDom();
                setMode('hod');
              }}
            >
              {copy.dom.walk}
            </button>
            <button
              type="button"
              className={`wb-tool ${mode === 'uredi' ? 'on' : ''}`.trim()}
              aria-pressed={mode === 'uredi'}
              onClick={() => {
                void api.dom.beginEdit().catch(() => undefined);
                setMode('uredi');
              }}
            >
              {copy.dom.edit}
            </button>
          </span>
        ) : (
          <Btn variant="ghost" size="small" onClick={() => setVisiting(null)}>
            {copy.dom.home}
          </Btn>
        )}
        <SaveChip save={save} />
      </div>

      {mode === 'uredi' && dom.mine ? (
        <Uredi world={dom.world} />
      ) : (
        <Walk
          world={dom.world}
          name={name}
          look={server.member.look}
          owner={host?.id ?? server.member.id}
          rev={dom.rev}
          live={dom.mine ? dom.vrata !== 'zatvoreno' : true}
        />
      )}

      {dom.mine ? (
        <Owner
          world={dom.world}
          ime={dom.ime}
          vrata={dom.vrata ?? 'zatvoreno'}
          kod={dom.kod ?? null}
          canRevert={dom.vrati === true}
          onVisit={setVisiting}
        />
      ) : null}
    </div>
  );
}

function SaveChip({ save }: { save: string }) {
  if (save === 'stale')
    return (
      <button type="button" className="wb-save bad" onClick={() => window.location.reload()}>
        {copy.dom.stale}
      </button>
    );
  if (save === 'error') return <span className="wb-save bad">{copy.dom.saveFailed}</span>;
  if (save === 'saving' || save === 'dirty') return <span className="wb-save">{copy.dom.saving}</span>;
  if (save === 'saved') return <span className="wb-save ok">{copy.dom.saved}</span>;
  return null;
}

/* ------------------------------------------------------------------ walking it */

/**
 * The island, walked.
 *
 * Rendered by `buildWorld` over a synthesised island with no nodes at all — `stampIsland` produces
 * no markers for one, and an `IslandInfo` whose `total` is zero draws its name with no fraction
 * under it. That last part is not incidental: „0 od 0" would be both wrong and the third number this
 * product does not have, and the free exercise island had to suppress the same thing in four places.
 */
function Walk({
  world,
  name,
  look,
  owner,
  rev,
  live,
}: {
  world: RawDom;
  name: string;
  look: number;
  owner: string;
  rev: number;
  /**
   * Whether to open a socket at all.
   *
   * On your own island with the door shut there is nobody who could be here, so there is nothing to
   * connect to — the same reasoning that keeps the map from opening one before the crew exists. An
   * unopened socket is not a degraded feature; it is the feature costing nothing.
   */
  live: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const liveRef = useRef<Uzivo | null>(null);
  /**
   * Which room to be in, kept in a ref rather than read from the closure.
   *
   * The engine's hooks are created once per world signature, and `rev` moves on every save — so a
   * hook that closed over it would keep asking for the room of the island as it was several strokes
   * ago. The signature does change on a save today, which would paper over it; that is an accident
   * of what a save happens to touch, not a guarantee.
   */
  const roomRef = useRef({ owner, rev });
  roomRef.current = { owner, rev };
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const hidden = useStore((s) => s.hidden);
  const [touch, setTouch] = useState(false);

  const souvenirs = useMemo(() => earnedSouvenirs(content, player), [content, player]);

  const model = useMemo(() => {
    const tiles = decodeTerrain(world.teren, world.sirina, world.visina);
    if (!tiles) return null;
    const island: ResolvedIsland = {
      id: 'dom',
      name,
      order: 0,
      day: 0,
      worksRequired: 0,
      souvenir: null,
      goal: null,
      biome: 'plicak',
      free: true,
      map: { x: Math.floor(world.sirina / 2), y: Math.floor(world.visina / 2) },
      world: {
        width: world.sirina,
        height: world.visina,
        tiles,
        dock: world.dok,
        spots: new Map(),
        characters: [],
        props: world.ukrasi.map((prop) => ({ kind: prop.vrsta, x: prop.x, y: prop.y })),
      },
      nodes: [],
      layoutIds: [],
    };
    const built = buildWorld([{ island, unlocked: true, visibleNodes: [] }]);
    const islands = new Map<string, IslandInfo>([
      [
        'dom',
        {
          name,
          biome: 'plicak',
          locked: false,
          blocker: null,
          done: 0,
          // Zero, so the label draws the island's name and nothing under it.
          total: 0,
          tagline: null,
        },
      ],
    ]);
    return { world: built, islands, dock: island.world!.dock };
  }, [name, world]);

  const signature = model?.world.signature ?? '';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !model) return;
    const placement = model.world.islands[0];
    const start = placement?.dock ?? {
      x: Math.floor(model.world.width / 2),
      y: Math.floor(model.world.height / 2),
    };

    const engine = new Engine(
      canvas,
      model.world,
      new Map<string, MarkerInfo>(),
      model.islands,
      decodeLook(look),
      souvenirs,
      start,
      {
        // Nothing on this island is a task, so none of these have anything to open.
        onInteract: () => {},
        onIslandTouch: () => {},
        onMove: () => {},
        onPeer: () => {},
        /**
         * Where I am, for whoever else is here.
         *
         * Straight tiles, no offset transform: a personal island has one grid and no archipelago
         * whose origin could slide, so the anchor is {0,0} and a tile means a tile. That is the same
         * reasoning `enterDom` states, arrived at from the other end.
         */
        onLive: (tile, facing, moving) => {
          const room = liveRef.current;
          if (!room) return;
          room.enterDom(roomRef.current.owner, roomRef.current.rev);
          room.report(tile, facing, moving);
        },
      },
    );
    engineRef.current = engine;
    engine.start();

    const keys: Record<string, Direction> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
      w: 'up',
      s: 'down',
      a: 'left',
      d: 'right',
    };
    const down = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return;
      const direction = keys[event.key];
      if (!direction) return;
      event.preventDefault();
      engine.press(direction);
    };
    const up = (event: KeyboardEvent) => {
      const direction = keys[event.key];
      if (direction) engine.release(direction);
    };
    const blur = () => engine.releaseAll();
    const resize = () => engine.resize();
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    window.addEventListener('resize', resize);
    const observer = new ResizeObserver(() => engine.resize());
    observer.observe(canvas);
    const first = () => setTouch(true);
    canvas.addEventListener('touchstart', first, { once: true, passive: true });

    return () => {
      engine.stop();
      engineRef.current = null;
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
      window.removeEventListener('resize', resize);
      observer.disconnect();
      canvas.removeEventListener('touchstart', first);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, look]);

  useEffect(() => {
    engineRef.current?.refresh(new Map(), model?.islands ?? new Map(), souvenirs);
  }, [model, souvenirs]);

  /**
   * The room, whose lifetime is the visit's rather than the engine's.
   *
   * Same shape as the map: the engine is rebuilt whenever the island's geometry changes, and tearing
   * down a working socket to redraw a beach would drop everybody standing on it for a moment every
   * time the owner painted a tile.
   */
  useEffect(() => {
    if (!live) return;
    const room = new Uzivo({ open: (url) => new WebSocket(url) as unknown as SocketLike });
    liveRef.current = room;
    engineRef.current?.setLive(room);
    return () => {
      room.leave();
      liveRef.current = null;
      engineRef.current?.setLive(null);
    };
  }, [live, owner]);

  // The engine is the one that gets rebuilt, so it has to be re-handed the room it does not own.
  useEffect(() => {
    engineRef.current?.setLive(liveRef.current);
    engineRef.current?.setHidden(new Set(hidden));
  }, [hidden, signature]);

  const hold = (direction: Direction) => ({
    onPointerDown: (event: React.PointerEvent) => {
      event.preventDefault();
      engineRef.current?.press(direction);
    },
    onPointerUp: () => engineRef.current?.release(direction),
    onPointerLeave: () => engineRef.current?.release(direction),
    onPointerCancel: () => engineRef.current?.release(direction),
  });

  if (!model) return <p className="tiny muted">{copy.error.genericBody}</p>;

  return (
    <div className="map-wrap dom-wrap">
      <canvas ref={canvasRef} className="map-canvas" role="img" aria-label={copy.dom.title} />
      {touch ? (
        <div className="dpad" aria-hidden="true">
          <span className="blank" />
          <button {...hold('up')}>↑</button>
          <span className="blank" />
          <button {...hold('left')}>←</button>
          <span className="blank" />
          <button {...hold('right')}>→</button>
          <span className="blank" />
          <button {...hold('down')}>↓</button>
          <span className="blank" />
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ rearranging it */

function Uredi({ world }: { world: RawDom }) {
  const [tool, setTool] = useState<BuilderTool>('cetka');
  const [terrain, setTerrain] = useState<Terrain>(Terrain.Ground);
  const [armed, setArmed] = useState<PropId | null>(null);
  const [zoom, setZoom] = useState(2);
  const [refusal, setRefusal] = useState<string | null>(null);

  const tiles = useMemo(
    () =>
      decodeTerrain(world.teren, world.sirina, world.visina) ??
      new Uint8Array(DOM_SIZE * DOM_SIZE).fill(Terrain.Sea),
    [world],
  );

  /**
   * Undo, member-sized.
   *
   * A short stack of whole islands rather than the builder's own history: an island is a few hundred
   * bytes and there is nothing here worth a second mechanism. Deliberately not persisted — coming
   * back tomorrow to a stack of yesterday's strokes would be a history UI, and „vrati kako je bilo"
   * is the answer to the case undo cannot reach.
   */
  const past = useRef<RawDom[]>([]);
  const [depth, setDepth] = useState(0);
  /**
   * True while the canvas has a stroke in flight.
   *
   * A second pointer landing on the CANVAS aborts the stroke, but a finger on the toolbar is not on
   * the canvas — so without this the ↶ beside the tools can fire mid-drag, and the undo is silently
   * painted back over by the next pointermove while the store keeps the reverted island. That is the
   * fourth invariant the shared canvas documents, and it is the one the parent has to hold.
   */
  const strokeLive = useRef(false);
  const remember = useCallback(() => {
    past.current.push(world);
    if (past.current.length > 30) past.current.shift();
    setDepth(past.current.length);
  }, [world]);
  const undo = useCallback(() => {
    if (strokeLive.current) return;
    const previous = past.current.pop();
    setDepth(past.current.length);
    if (previous) editDom(previous);
  }, []);

  /** What the canvas calls to take back a stroke it has already dropped. Never guarded. */
  const undoNow = useCallback(() => {
    const previous = past.current.pop();
    setDepth(past.current.length);
    if (previous) editDom(previous);
  }, []);

  const paint = useCallback(
    (next: Uint8Array, coalesce: boolean) => {
      if (!coalesce) remember();
      editDom({ ...world, teren: encodeTerrain(next, world.sirina, world.visina) });
    },
    [remember, world],
  );

  const propAt = useCallback(
    (x: number, y: number) => world.ukrasi.findIndex((prop) => prop.x === x && prop.y === y),
    [world],
  );

  /**
   * One reason at a time, said at the cursor, as a fact about the world rather than a verdict.
   *
   * The full-island cap is phrased the same way — „Tu nema mesta." — because it is a property of the
   * place and not a permission somebody lacks. That distinction is the whole difference between a
   * ceiling and a fee.
   */
  const objects = useMemo(
    () => ({
      hitTest: (x: number, y: number) => {
        const index = propAt(x, y);
        return index >= 0 ? index : null;
      },
      move: (index: number, x: number, y: number, coalesce: boolean) => {
        // The first step of a drag is the undo point; every step after it continues the same one, so
        // dragging something across the island is one press of ↶ rather than forty.
        if (!coalesce) remember();
        editDom({
          ...world,
          ukrasi: world.ukrasi.map((prop, at) => (at === index ? { ...prop, x, y } : prop)),
        });
      },
      remove: (index: number) => {
        remember();
        editDom({ ...world, ukrasi: world.ukrasi.filter((_, at) => at !== index) });
        return true;
      },
      place: (x: number, y: number) => {
        if (!armed) return false;
        const ground = (tiles[y * world.sirina + x] ?? Terrain.Sea) as Terrain;
        if (isWater(ground)) {
          setRefusal(copy.teren.noWater);
          return false;
        }
        if (isSolid(ground)) {
          setRefusal(copy.teren.noSolid);
          return false;
        }
        if (propAt(x, y) >= 0) {
          setRefusal(copy.teren.noTaken);
          return false;
        }
        if (world.ukrasi.length >= DOM_MAX_PROPS) {
          setRefusal(copy.dom.full);
          return false;
        }
        setRefusal(null);
        remember();
        editDom({ ...world, ukrasi: [...world.ukrasi, { vrsta: armed, x, y, izvor: 'osnovno' }] });
        return true;
      },
      armed: armed !== null,
    }),
    [armed, propAt, remember, tiles, world],
  );

  const overlay = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      for (const prop of world.ukrasi) {
        const art = propArt(prop.vrsta);
        if (!art) continue;
        const sprite = bake(`wb:prop:${prop.vrsta}`, art.frames[0]!, spriteColors);
        ctx.drawImage(sprite.canvas, prop.x * TILE, prop.y * TILE - art.lift);
      }
      // The dock, as a mark rather than a sprite: it is where a guest arrives, not a thing standing
      // on the island, and it has to stay readable under whatever was painted there.
      ctx.strokeStyle = '#ffc247';
      ctx.lineWidth = 2;
      ctx.strokeRect(world.dok.x * TILE + 1, world.dok.y * TILE + 1, TILE - 2, TILE - 2);
    },
    [world],
  );

  return (
    <div className="dom-edit">
      <div className="wb-top dom-tools">
        <span className="wb-group" role="group" aria-label={copy.teren.tools}>
          {DOM_TOOLS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`wb-tool ${tool === entry.id ? 'on' : ''}`.trim()}
              aria-pressed={tool === entry.id}
              onClick={() => {
                setTool(entry.id);
                setArmed(null);
                setRefusal(null);
              }}
            >
              {entry.label}
            </button>
          ))}
        </span>
        <span className="wb-group">
          <button type="button" className="wb-tool" disabled={depth === 0} title={copy.teren.undo} onClick={undo}>
            ↶
          </button>
          <button
            type="button"
            className="wb-tool"
            title={copy.teren.zoomOut}
            disabled={zoom <= 1}
            onClick={() => setZoom(zoom - 1)}
          >
            −
          </button>
          <span className="wb-zoom">{zoom}×</span>
          <button
            type="button"
            className="wb-tool"
            title={copy.teren.zoomIn}
            disabled={zoom >= 6}
            onClick={() => setZoom(zoom + 1)}
          >
            +
          </button>
        </span>
      </div>

      <div className="wb-body">
        <div className="wb-stage">
          <BuilderCanvas
            width={world.sirina}
            height={world.visina}
            tiles={tiles}
            biome="plicak"
            label={copy.dom.title}
            tool={tool}
            terrain={terrain}
            brush={1}
            grid
            zoom={zoom}
            onZoom={setZoom}
            onPaint={paint}
            onSample={setTerrain}
            objects={objects}
            onPick={() => {}}
            onHover={(at: Tile | null) => {
              if (!at) setRefusal(null);
            }}
            onUndo={undoNow}
            onStroke={(live: boolean) => {
              strokeLive.current = live;
            }}
            overlay={overlay}
          />
          <p className={`tiny wb-hint ${refusal ? 'ed-fielderror' : 'muted'}`}>
            {refusal ??
              (tool === 'gumica'
                ? copy.dom.eraseHint
                : tool === 'objekti'
                  ? armed
                    ? copy.teren.placeHint
                    : copy.dom.dragHint
                  : copy.dom.paintHint)}
          </p>
        </div>

        <div className="wb-side">
          {tool === 'objekti' ? (
            <div className="wb-panel">
              <b className="lbl">{copy.teren.paletteObjects}</b>
              <div className="wb-chips">
                {domPropKinds().map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    className={`wb-object ${armed === kind ? 'on' : ''}`.trim()}
                    onClick={() => setArmed(armed === kind ? null : kind)}
                  >
                    <PropChip kind={kind} />
                    <span className="tiny">{PROPS[kind]?.label ?? kind}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <TerrainPalette
              biome="plicak"
              value={terrain}
              onChange={setTerrain}
              values={DOM_TERRAIN}
              title={copy.dom.paintWhat}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ the owner's controls */

function Owner({
  world,
  ime,
  vrata,
  kod,
  canRevert,
  onVisit,
}: {
  world: RawDom;
  ime: [number | null, number | null];
  vrata: DomDoor;
  kod: string | null;
  canRevert: boolean;
  onVisit: (id: string | null) => void;
}) {
  const [doorError, setDoorError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [open, setOpen] = useState<Array<{ id: string; name: string; ime: [number | null, number | null] }>>([]);

  useEffect(() => {
    void api.dom
      .open()
      .then((result) => setOpen(result.ostrva))
      .catch(() => setOpen([]));
  }, []);

  const setDoor = async (next: DomDoor) => {
    setDoorError(await doorDom(next));
  };

  return (
    <div className="dom-panels">
      <div className="wb-panel">
        <b className="lbl">{copy.dom.name}</b>
        <div className="dom-namepick">
          <select
            className="field"
            value={ime[0] ?? 0}
            onChange={(event) => void nameDom(Number(event.target.value), ime[1] ?? 0)}
          >
            {DOM_WORDS_A.map((word, index) => (
              <option key={word} value={index}>
                {word}
              </option>
            ))}
          </select>
          <select
            className="field"
            value={ime[1] ?? 0}
            onChange={(event) => void nameDom(ime[0] ?? 0, Number(event.target.value))}
          >
            {DOM_WORDS_B.map((word, index) => (
              <option key={word} value={index}>
                {word}
              </option>
            ))}
          </select>
        </div>
        <span className="tiny muted">{copy.dom.nameHint}</span>
      </div>

      <div className="wb-panel">
        <b className="lbl">{copy.dom.door}</b>
        <div className="wb-group">
          {(
            [
              ['zatvoreno', copy.dom.doorClosed],
              ['kod', copy.dom.doorCode],
              ['otvoreno', copy.dom.doorOpen],
            ] as Array<[DomDoor, string]>
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`wb-tool ${vrata === value ? 'on' : ''}`.trim()}
              aria-pressed={vrata === value}
              onClick={() => void setDoor(value)}
            >
              {label}
            </button>
          ))}
        </div>
        {doorError ? <p className="ed-fielderror tiny">{doorError}</p> : null}
        {vrata === 'kod' && kod ? (
          <>
            <p className="dom-code verbatim">{formatDomCode(kod)}</p>
            <span className="tiny muted">{copy.dom.doorCodeHint}</span>
            <Btn variant="ghost" size="small" onClick={() => void setDoor('kod')}>
              {copy.dom.newCode}
            </Btn>
          </>
        ) : null}
        {canRevert ? (
          <div className="btn-row">
            <Btn
              variant="ghost"
              size="small"
              onClick={() => {
                void revertDom();
                sfx.bump();
              }}
            >
              {copy.dom.revert}
            </Btn>
          </div>
        ) : null}
      </div>

      <div className="wb-panel">
        <b className="lbl">{copy.dom.visit}</b>
        <div className="dom-namepick">
          <input
            className="field"
            value={code}
            placeholder={copy.dom.visitCode}
            onChange={(event) => setCode(normaliseDomCode(event.target.value))}
          />
        </div>
        {codeError ? <p className="ed-fielderror tiny">{codeError}</p> : null}
        <span className="tiny muted">{copy.dom.visitHint}</span>

        <b className="lbl wb-sublabel">{copy.dom.open}</b>
        {open.length === 0 ? (
          <p className="tiny muted">{copy.dom.openEmpty}</p>
        ) : (
          <div className="wb-objectlist">
            {open.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className="wb-object"
                onClick={() => {
                  void api.dom.knock(entry.id).catch(() => undefined);
                  onVisit(entry.id);
                }}
              >
                <span className="wb-objectname">{domName(entry.ime[0], entry.ime[1])}</span>
                <span className="tiny muted">{entry.name}</span>
              </button>
            ))}
          </div>
        )}
        {/* The box only becomes an action once a whole code is in it: a half-typed one is not a
            mistake worth an error message. */}
        {looksLikeDomCode(code) ? (
          <Btn
            variant="primary"
            size="small"
            onClick={async () => {
              setCodeError(null);
              try {
                const result = await api.dom.enter(code);
                setCode('');
                onVisit(result.id);
              } catch {
                setCodeError(copy.dom.visitBadCode);
              }
            }}
          >
            {copy.dom.visitGo}
          </Btn>
        ) : null}
      </div>

      <span className="tiny muted">
        {world.ukrasi.length >= DOM_MAX_PROPS ? copy.dom.full : copy.dom.intro}
      </span>
    </div>
  );
}
